import Notify from 'quasar/src/plugins/Notify.js';;
import i18n from '@/i18n';
import { isConfigurationFeatureEnabled } from '@/utils/configuration-feature-flags';

/**
 * Whitelist des destinataires des environnements de test (DEV / UAT).
 *
 * Portée par la ligne `RECIPIENT_WHITELIST` de `configuration_config_param` :
 * `_value = 'true'` active la restriction et l'écran de paramétrage, la liste vit dans
 * `_business_data` (`{ emails, domains, phones }`). Ligne absente ou `false` : rien ne change.
 *
 * Le front informe l'utilisateur et n'appelle pas le serveur quand aucun destinataire n'est
 * autorisé ; le filtrage destinataire par destinataire est fait par odm-contactmechanism,
 * qui couvre aussi les envois backend (signature, Camunda…).
 */
export const RECIPIENT_WHITELIST_PARAM_ID = 'RECIPIENT_WHITELIST';

export interface RecipientWhitelist {
  emails: string[];
  domains: string[];
  phones: string[];
}

export type RecipientChannel = 'email' | 'sms';

/** Paramètres des templates appelés par le front qui portent les destinataires. */
const RECIPIENT_PARAMETERS: Record<RecipientChannel, string[]> = {
  email: ['signatory_email', 'recipient_email', 'email', 'signatory_cc', 'attendeeEmail', 'organizerEmail'],
  sms: ['recipient'],
};

/** Relu au plus une fois par minute ; l'écran de paramétrage le met à jour à l'enregistrement. */
const CACHE_DURATION_MS = 60_000;
let cache: { whitelist: RecipientWhitelist; loadedAt: number } | null = null;

export function normalizeEmail(email: string): string {
  return (email || '').trim().toLowerCase();
}

export function normalizeDomain(domain: string): string {
  return normalizeEmail(domain).replace(/^@/, '');
}

/** Forme canonique, identique au back : chiffres seuls, "00" et "+33" ramenés à la forme nationale. */
export function normalizePhone(phone: string): string {
  let normalized = (phone || '').replace(/[^\d+]/g, '');
  if (normalized.startsWith('00')) normalized = '+' + normalized.substring(2);
  if (normalized.startsWith('+33')) normalized = '0' + normalized.substring(3);
  return normalized;
}

export function isRecipientWhitelistEnabled(): boolean {
  return isConfigurationFeatureEnabled(RECIPIENT_WHITELIST_PARAM_ID);
}

/** Lit la liste depuis le businessData du paramètre, en tolérant un contenu incomplet. */
export function toRecipientWhitelist(businessData: any): RecipientWhitelist {
  const list = (field: string): string[] =>
    Array.isArray(businessData?.[field]) ? businessData[field].filter((v: any) => typeof v === 'string' && v.trim()) : [];
  return { emails: list('emails'), domains: list('domains'), phones: list('phones') };
}

export function setCachedRecipientWhitelist(whitelist: RecipientWhitelist) {
  cache = { whitelist, loadedAt: Date.now() };
}

/** @returns la whitelist si la restriction est active, sinon null */
export async function loadRecipientWhitelist(): Promise<RecipientWhitelist | null> {
  if (!isRecipientWhitelistEnabled()) return null;
  if (cache && Date.now() - cache.loadedAt < CACHE_DURATION_MS) return cache.whitelist;
  try {
    // Import différé : contactMechanism.ts est chargé pendant l'initialisation de @/utils,
    // et configParamService lit UON dès son chargement (import circulaire).
    const { getConfigParam } = await import('@/store/services/configuration/configParamService');
    const param = await getConfigParam(RECIPIENT_WHITELIST_PARAM_ID);
    setCachedRecipientWhitelist(toRecipientWhitelist(param?.businessData));
    return cache!.whitelist;
  } catch (e) {
    // Le serveur filtre de toute façon : on n'empêche pas l'envoi faute d'avoir pu lire la liste.
    console.warn('loadRecipientWhitelist', e);
    return null;
  }
}

export function isEmailAllowed(whitelist: RecipientWhitelist, email: string): boolean {
  const normalized = normalizeEmail(email);
  if (!normalized) return false;
  const domain = normalized.substring(normalized.lastIndexOf('@') + 1);
  return whitelist.emails.some((e) => normalizeEmail(e) === normalized)
    || (normalized.includes('@') && whitelist.domains.some((d) => normalizeDomain(d) === domain));
}

export function isPhoneAllowed(whitelist: RecipientWhitelist, phone: string): boolean {
  const normalized = normalizePhone(phone);
  return !!normalized && whitelist.phones.some((p) => normalizePhone(p) === normalized);
}

function notifyBlockedRecipients(blocked: string[], channel: RecipientChannel) {
  Notify.create({
    type: 'warning',
    message: i18n.global.t(`recipientWhitelist.alert.${channel}`),
    caption: `${i18n.global.t('recipientWhitelist.alert.blockedRecipients')} ${blocked.join(', ')}`,
    multiLine: true,
    timeout: 10000,
    actions: [{ icon: 'close', color: 'white' }],
  });
}

/**
 * Contrôle les destinataires d'une demande d'envoi avant l'appel au serveur et affiche
 * la liste de ceux qui ne recevront pas le message.
 *
 * @returns false si aucun destinataire n'est autorisé (l'envoi est alors inutile)
 */
export async function checkRecipientWhitelist(request: { parameters?: Record<string, any> }, channel: RecipientChannel): Promise<boolean> {
  const whitelist = await loadRecipientWhitelist();
  if (!whitelist || !request?.parameters) return true;

  const recipients = new Set<string>();
  for (const key of RECIPIENT_PARAMETERS[channel]) {
    const value = request.parameters[key];
    if (typeof value !== 'string') continue;
    const values = channel === 'email' ? value.split(/[;,]/) : [value];
    values.map((v) => v.trim()).filter(Boolean).forEach((v) => recipients.add(v));
  }
  if (recipients.size === 0) return true;

  const allowed = (r: string) => (channel === 'email' ? isEmailAllowed(whitelist, r) : isPhoneAllowed(whitelist, r));
  const blocked = [...recipients].filter((r) => !allowed(r));
  if (blocked.length > 0) notifyBlockedRecipients(blocked, channel);
  return blocked.length < recipients.size;
}
